import { useState, useCallback, useMemo } from 'react';
import { Review } from '../components/pwatv/UserReviewSlider/UserReviewSlider';

interface UseReviewSyncOptions {
  reviews: Review[];
  contextualTextTemplate?: (review: Review) => string;
}

interface UseReviewSyncReturn {
  currentReview: Review | null;
  handleReviewChange: (review: Review) => void;
  getContextualText: () => string;
}

/**
 * Default template for contextual text
 */
const DEFAULT_CONTEXTUAL_TEXT_TEMPLATE = (review: Review): string => {
  return `${review.name} from ${review.location} took FREE trial 🎉`;
};

/**
 * Custom hook to synchronize review slider with contextual text
 * 
 * This hook manages the state synchronization between a review slider component
 * and any component that needs to display contextual text based on the current review.
 * 
 * @param reviews - Array of review objects
 * @param contextualTextTemplate - Optional function to customize the contextual text format.
 *                                  If not provided, uses the default template.
 * @returns Object containing:
 *   - currentReview: The currently selected review
 *   - handleReviewChange: Callback function to update the current review
 *   - getContextualText: Function that returns the formatted contextual text
 * 
 * @example
 * ```tsx
 * const { handleReviewChange, getContextualText } = useReviewSync({
 *   reviews,
 *   contextualTextTemplate: (review) => `${review.name} from ${review.location} took FREE trial 🎉`
 * });
 * 
 * <UserReviewSlider reviews={reviews} onReviewChange={handleReviewChange} />
 * <TVStickyButton contextualText={getContextualText()} />
 * ```
 */
export const useReviewSync = ({
  reviews,
  contextualTextTemplate,
}: UseReviewSyncOptions): UseReviewSyncReturn => {
  const [currentReview, setCurrentReview] = useState<Review | null>(
    reviews.length > 0 ? reviews[0] : null
  );

  const handleReviewChange = useCallback((review: Review) => {
    setCurrentReview(review);
  }, []);

  // Memoize the template function to avoid recreating it on every render
  const template = useMemo(
    () => contextualTextTemplate || DEFAULT_CONTEXTUAL_TEXT_TEMPLATE,
    [contextualTextTemplate]
  );

  // Memoize the default review to avoid recalculating
  const defaultReview = useMemo(
    () => (reviews.length > 0 ? reviews[0] : null),
    [reviews]
  );

  const getContextualText = useCallback((): string => {
    const review = currentReview || defaultReview;
    
    if (!review) {
      return '';
    }

    return template(review);
  }, [currentReview, defaultReview, template]);

  return {
    currentReview,
    handleReviewChange,
    getContextualText,
  };
};

